// Form controls.
//
// Primitives (TextInput, SelectInput, Textarea, FormField) for forms that
// submit as a whole, like Login and the New-tenant wizard.
//
// EditableTextField / EditableSelectField for settings: each field saves
// itself (text on blur or Enter, select on change), Escape reverts, and the
// outcome shows next to the label. Only the changed field is sent, so one
// field's save can never overwrite another's.

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/lib/format'
import { EyeIcon, EyeOffIcon } from './icons'
import { SaveStatus } from './SaveStatus'
import { useSaveAction } from './useSaveAction'

const CONTROL =
  // 16px below md: iOS Safari zooms the page on focus for anything smaller.
  'w-full rounded-md border bg-raised px-3 text-[13.5px] max-md:text-base text-ink placeholder:text-subtle ' +
  'transition-colors focus:outline-none focus-visible:outline-none focus:border-accent focus:ring-2 focus:ring-accent/25 ' +
  'disabled:cursor-not-allowed disabled:opacity-60'

function controlClasses(invalid?: boolean, extra?: string) {
  return cn(CONTROL, invalid ? 'border-bad/70' : 'border-line-strong hover:border-subtle', extra)
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  mono?: boolean
}

/** A text input. `type="password"` adds a show/hide button inside the right edge. */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput({ invalid, mono, className, type, ...rest }, ref) {
  if (type === 'password') return <PasswordInput ref={ref} invalid={invalid} mono={mono} className={className} {...rest} />
  return <input ref={ref} type={type} aria-invalid={invalid || undefined} className={controlClasses(invalid, cn('h-9', mono && 'font-mono text-[13px]', className))} {...rest} />
})

// TextInput's password variant: the input plus an eye button that switches it
// between hidden and shown. Starts hidden on every mount and goes back to
// hidden when its form submits (the native event, so Enter and the button both
// count). The button keeps focus in the field and the caret where it was. Only
// password inputs get the wrapper, so every other TextInput keeps its markup.
const PasswordInput = forwardRef<HTMLInputElement, Omit<TextInputProps, 'type'>>(function PasswordInput({ invalid, mono, className, disabled, ...rest }, ref) {
  const [shown, setShown] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const sel = useRef<[number | null, number | null] | null>(null)
  const setRef = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) ref.current = el
    },
    [ref],
  )

  useEffect(() => {
    const form = inputRef.current?.form
    if (!shown || !form) return
    const hide = () => setShown(false)
    form.addEventListener('submit', hide)
    return () => form.removeEventListener('submit', hide)
  }, [shown])

  // Some browsers move the caret when an input's type changes; put it back.
  useLayoutEffect(() => {
    const el = inputRef.current
    const s = sel.current
    sel.current = null
    if (el && s && document.activeElement === el && s[0] != null) el.setSelectionRange(s[0], s[1] ?? s[0])
  }, [shown])

  return (
    <div className="relative">
      <input
        ref={setRef}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        {...rest}
        type={shown ? 'text' : 'password'}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={controlClasses(invalid, cn('h-9 pr-11', mono && 'font-mono text-[13px]', className))}
      />
      <button
        type="button"
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        aria-controls={rest.id}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const el = inputRef.current
          sel.current = el ? [el.selectionStart, el.selectionEnd] : null
          setShown((s) => !s)
        }}
        className={cn(
          'absolute right-px top-px bottom-px grid w-11 place-items-center rounded-r-md text-muted transition-colors hover:bg-hover hover:text-ink focus-visible:outline-offset-[-2px]',
          "before:absolute before:inset-x-0 before:-inset-y-[5px] before:content-['']",
          'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent',
        )}
      >
        {shown ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
      </button>
    </div>
  )
})

export interface SelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>
  /** Options under headings (<optgroup>), after `options`. */
  groups?: ReadonlyArray<{ label: string; options: ReadonlyArray<{ value: string; label: string }> }>
}

const CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238C97A6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")"

export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(function SelectInput({ invalid, options, groups, className, style, ...rest }, ref) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={controlClasses(invalid, cn('h-9 appearance-none bg-no-repeat pr-9', className))}
      style={{ backgroundImage: CHEVRON, backgroundSize: '16px', backgroundPosition: 'right 0.6rem center', ...style }}
      {...rest}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
      {groups?.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ invalid, className, ...rest }, ref) {
  return <textarea ref={ref} aria-invalid={invalid || undefined} className={controlClasses(invalid, cn('min-h-[5rem] py-2 leading-relaxed', className))} {...rest} />
})

export interface FormFieldProps {
  label: ReactNode
  /** id of the control inside; generated if omitted. */
  htmlFor?: string
  hint?: ReactNode
  error?: string | null
  /** Shown at the right of the label row (e.g. SaveStatus). */
  aside?: ReactNode
  required?: boolean
  className?: string
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
}

/** Label + control + hint + error, wired with ids for screen readers. */
export function FormField({ label, htmlFor, hint, error, aside, required, className, children }: FormFieldProps) {
  const generated = useId()
  const id = htmlFor ?? generated
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined
  return (
    <div className={cn('grid gap-1.5', className)}>
      <div className="flex min-h-[1.25rem] flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <label htmlFor={id} className="text-xs font-semibold text-ink">
          {label}
          {required ? (
            <span aria-hidden className="ml-0.5 text-accent">
              *
            </span>
          ) : null}
        </label>
        {aside}
      </div>
      {children({ id, describedBy, invalid: !!error })}
      {error ? (
        <p id={errorId} className="text-xs text-bad">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

// ─── Self-saving fields ─────────────────────────────────────────────────────

interface EditableBase<T> {
  label: ReactNode
  value: T
  /** Persist. Throw (or reject) to show the error inline. */
  onSave: (next: T) => Promise<unknown>
  hint?: ReactNode
  disabled?: boolean
  className?: string
}

export interface EditableTextFieldProps extends EditableBase<string> {
  /** Return an error message to block the save. */
  validate?: (next: string) => string | null
  placeholder?: string
  mono?: boolean
  multiline?: boolean
  maxLength?: number
  /** Normalize before compare/save (default: trim). */
  normalize?: (raw: string) => string
}

/**
 * Text that saves itself on blur or Enter (Cmd/Ctrl+Enter when multiline).
 * Escape reverts. While the user isn't editing, it follows `value` from the
 * server, so a refetch after another field's save shows fresh data.
 */
export function EditableTextField({
  label,
  value,
  onSave,
  hint,
  disabled,
  className,
  validate,
  placeholder,
  mono,
  multiline,
  maxLength,
  normalize = (s) => s.trim(),
}: EditableTextFieldProps) {
  const [draft, setDraft] = useState(value)
  const [invalid, setInvalid] = useState<string | null>(null)
  const editing = useRef(false)
  // Escape reverts and then blurs; the blur must not save the stale draft.
  const reverting = useRef(false)
  const save = useSaveAction()

  useEffect(() => {
    if (!editing.current) setDraft(value)
  }, [value])

  const commit = async () => {
    if (reverting.current) {
      reverting.current = false
      return
    }
    editing.current = false
    const next = normalize(draft)
    if (next === normalize(value)) {
      setDraft(value)
      setInvalid(null)
      return
    }
    const problem = validate?.(next) ?? null
    setInvalid(problem)
    if (problem) {
      editing.current = true
      return
    }
    setDraft(next)
    await save.run(() => onSave(next))
  }

  const revert = () => {
    reverting.current = true
    editing.current = false
    setDraft(value)
    setInvalid(null)
    save.reset()
  }

  return (
    <FormField label={label} hint={hint} error={invalid} aside={<SaveStatus state={save} onRetry={commit} />} className={className}>
      {({ id, describedBy, invalid: isInvalid }) =>
        multiline ? (
          <Textarea
            id={id}
            aria-describedby={describedBy}
            invalid={isInvalid || save.status === 'error'}
            value={draft}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            onChange={(e) => {
              editing.current = true
              setDraft(e.target.value)
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                revert()
                ;(e.target as HTMLTextAreaElement).blur()
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) (e.target as HTMLTextAreaElement).blur()
            }}
          />
        ) : (
          <TextInput
            id={id}
            aria-describedby={describedBy}
            invalid={isInvalid || save.status === 'error'}
            mono={mono}
            value={draft}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            onChange={(e) => {
              editing.current = true
              setDraft(e.target.value)
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                revert()
                ;(e.target as HTMLInputElement).blur()
              }
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            }}
          />
        )
      }
    </FormField>
  )
}

export interface EditableSelectFieldProps extends EditableBase<string> {
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>
}

/** A select that saves on change and rolls back if the save fails. */
export function EditableSelectField({ label, value, onSave, options, hint, disabled, className }: EditableSelectFieldProps) {
  const [draft, setDraft] = useState(value)
  const [attempt, setAttempt] = useState(value)
  const save = useSaveAction()

  useEffect(() => {
    setDraft(value)
  }, [value])

  const commit = async (next: string) => {
    setAttempt(next)
    setDraft(next)
    const ok = await save.run(() => onSave(next))
    if (!ok) setDraft(value)
  }

  return (
    <FormField label={label} hint={hint} aside={<SaveStatus state={save} onRetry={() => commit(attempt)} />} className={className}>
      {({ id, describedBy }) => (
        <SelectInput
          id={id}
          aria-describedby={describedBy}
          invalid={save.status === 'error'}
          value={draft}
          options={options}
          // Stays focusable while saving (disabling it would drop keyboard
          // focus); a change during a save is ignored.
          disabled={disabled}
          aria-busy={save.status === 'saving' || undefined}
          onChange={(e) => {
            if (save.status !== 'saving') commit(e.target.value)
          }}
        />
      )}
    </FormField>
  )
}
